import { useNavigate, useLocation } from "react-router-dom";
import { useMe } from "@/hooks/useMe";
import { useAuth } from "@/auth/useAuth";
import {
  Users,
  Briefcase,
  Landmark,
  ClipboardList,
  ShieldCheck,
  Home,
  LogOut,
} from "lucide-react";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/components/ui/tooltip";

interface NavItem {
  label: string;
  icon: typeof Users;
  path: string;
  requires?: "admin" | "can_view_clients" | "can_view_funds" | "can_view_assets" | "can_view_investments";
  admin?: boolean;
}

const NAV_ITEMS: NavItem[] = [
  { label: "Home",      icon: Home,      path: "/" },
  { label: "Investors", icon: Users,     path: "/clients", requires: "can_view_clients" },
  { label: "Funds",     icon: Briefcase, path: "/funds",   requires: "can_view_funds" },
  { label: "Assets",    icon: Landmark,  path: "/assets",  requires: "can_view_assets" },
];

const ADMIN_ITEMS: NavItem[] = [
  { label: "Logs",  icon: ClipboardList, path: "/audit-logs", admin: true },
  { label: "Users", icon: ShieldCheck,   path: "/users",      admin: true },
];

export function Sidebar() {
  const navigate = useNavigate();
  const location = useLocation();
  const { signOut } = useAuth();
  const { data: me } = useMe();
  const isAdmin = me?.user.isAdmin ?? false;
  const perms = me?.permissions;

  const visibleMain = NAV_ITEMS.filter((item) => {
    if (!item.requires) return true;
    if (item.requires === "admin") return isAdmin;
    return isAdmin || perms?.[item.requires];
  });

  const isActive = (path: string) =>
    path === "/" ? location.pathname === "/" : location.pathname.startsWith(path);

  const NavBtn = ({ item }: { item: NavItem }) => {
    const Icon = item.icon;
    const active = isActive(item.path);
    return (
      <Tooltip>
        <TooltipTrigger asChild>
          <button
            onClick={() => navigate(item.path)}
            className="flex items-center justify-center w-10 h-10 rounded-lg transition-colors"
            style={{
              backgroundColor: active ? "rgba(255,255,255,0.2)" : "transparent",
              color: "white",
            }}
            onMouseEnter={(e) => {
              if (!active) (e.currentTarget as HTMLButtonElement).style.backgroundColor = "rgba(255,255,255,0.1)";
            }}
            onMouseLeave={(e) => {
              if (!active) (e.currentTarget as HTMLButtonElement).style.backgroundColor = "transparent";
            }}
          >
            <Icon className="h-5 w-5" />
          </button>
        </TooltipTrigger>
        <TooltipContent side="right">{item.label}</TooltipContent>
      </Tooltip>
    );
  };

  return (
    <TooltipProvider delayDuration={300}>
      <aside
        className="fixed left-0 top-0 h-full w-14 flex flex-col items-center gap-1 z-50"
        style={{ backgroundColor: "rgb(0, 56, 130)" }}
      >
        {/* Spacer matching header height */}
        <div className="h-20 w-full shrink-0" />

        {/* Main nav */}
        {visibleMain.map((item) => (
          <NavBtn key={item.path} item={item} />
        ))}

        {/* Admin separator + items */}
        {isAdmin && (
          <>
            <div className="my-2 w-6 border-t border-white/20" />
            {ADMIN_ITEMS.map((item) => (
              <NavBtn key={item.path} item={item} />
            ))}
          </>
        )}

        {/* Sign out at bottom */}
        <div className="mt-auto">
          <Tooltip>
            <TooltipTrigger asChild>
              <button
                onClick={() => signOut()}
                className="flex items-center justify-center w-10 h-10 rounded-lg transition-colors"
                style={{ color: "rgba(255,255,255,0.6)" }}
                onMouseEnter={(e) => {
                  (e.currentTarget as HTMLButtonElement).style.backgroundColor = "rgba(255,255,255,0.1)";
                  (e.currentTarget as HTMLButtonElement).style.color = "white";
                }}
                onMouseLeave={(e) => {
                  (e.currentTarget as HTMLButtonElement).style.backgroundColor = "transparent";
                  (e.currentTarget as HTMLButtonElement).style.color = "rgba(255,255,255,0.6)";
                }}
              >
                <LogOut className="h-5 w-5" />
              </button>
            </TooltipTrigger>
            <TooltipContent side="right">Sign out</TooltipContent>
          </Tooltip>
        </div>
      </aside>
    </TooltipProvider>
  );
}
